import React, { useState } from "react";
import { Heart, Wind, Sparkles } from "lucide-react";
import { 
  WHY_PETS_MATTER, 
  SHOW_LOVE_TIPS, 
  BREATHING_EXERCISES, 
  RELAXATION_TIPS 
} from "../data/constants";

export const ComfortCornerPage = () => {
  const [activeExercise, setActiveExercise] = useState(null);

  return (
    <div data-testid="comfort-page" className="fade-in relative z-10">
      <h1 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">Comfort Corner 🕯️</h1>
      <p className="font-handwritten text-2xl text-sage mb-8">A peaceful space for relaxation, reflection, and connection 💆</p>

      {/* Why Pets Matter */}
      <div className="mb-10">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 border border-amber-200">
          <h2 className="font-heading text-2xl font-semibold text-charcoal mb-2 flex items-center gap-3">
            <span className="text-3xl">🐾</span> Why Pets Matter
          </h2>
          <p className="text-stone mb-6">Pets shape us. They teach us what we often forget.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {WHY_PETS_MATTER.map((item, index) => (
              <div key={index} className="bg-white/80 rounded-2xl p-5 shadow-sm">
                <h3 className="font-heading text-lg font-semibold text-charcoal mb-1">{item.lesson}</h3>
                <p className="text-stone text-sm">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <p className="text-charcoal font-medium">They turn ordinary days into something meaningful.</p>
            <p className="text-stone mt-2">Even in the hardest times, they remain a steady light.</p>
            <p className="font-handwritten text-xl text-amber-700 mt-4">Pets remind us that comfort is real — and kindness matters.</p>
          </div>
        </div>
      </div>

      {/* How to Show Love Every Day */}
      <div className="mb-10">
        <h2 className="font-heading text-2xl font-semibold text-charcoal mb-2 flex items-center gap-3">
          <Heart size={24} className="text-rose-500" /> How to Show Love Every Day
        </h2>
        <p className="text-stone mb-6">Love is in the small, consistent things — not grand gestures. Try one or two:</p>
        <div className="grid md:grid-cols-3 gap-4">
          {SHOW_LOVE_TIPS.map((item, index) => (
            <div key={index} className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-sm border border-white/50 hover:shadow-md transition-all">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">{item.emoji}</span>
                <h3 className="font-heading font-semibold text-charcoal">{item.tip}</h3>
              </div>
              <p className="text-stone text-sm">{item.description}</p>
            </div>
          ))}
        </div>
        <p className="text-center mt-6 font-handwritten text-xl text-rose-600">Love is consistent. 💕</p>
      </div>

      {/* Breathing Exercises */}
      <div className="mb-10">
        <h2 className="font-heading text-2xl font-semibold text-charcoal mb-4 flex items-center gap-2">
          <Wind size={24} className="text-sage" /> Breathing Exercises
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {BREATHING_EXERCISES.map((exercise, index) => (
            <div 
              key={index} 
              data-testid={`breathing-exercise-${index}`}
              className={`bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/50 cursor-pointer transition-all hover:shadow-md hover:-translate-y-1 ${activeExercise === index ? 'ring-2 ring-sage' : ''}`}
              onClick={() => setActiveExercise(activeExercise === index ? null : index)}
            >
              <div className="text-4xl mb-3">{exercise.emoji}</div>
              <h3 className="font-heading text-lg font-semibold text-charcoal mb-2">{exercise.name}</h3>
              <p className="text-stone text-sm mb-3">{exercise.description}</p>
              <span className="inline-block px-3 py-1 bg-sage/10 text-sage text-xs rounded-full">{exercise.duration}</span>
              
              {activeExercise === index && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="font-medium text-charcoal mb-3">Steps:</h4>
                  <ol className="space-y-2">
                    {exercise.steps.map((step, stepIdx) => (
                      <li key={stepIdx} className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-sage text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">{stepIdx + 1}</span>
                        <span className="text-stone">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Relaxation Tips */}
      <div className="mb-10">
        <h2 className="font-heading text-2xl font-semibold text-charcoal mb-4 flex items-center gap-2">
          <Sparkles size={24} className="text-terracotta" /> Relaxation Tips with Your Pet
        </h2>
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/50">
          <div className="grid md:grid-cols-2 gap-4">
            {RELAXATION_TIPS.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4 rounded-2xl hover:bg-sage/5 transition-all">
                <span className="text-3xl">{item.emoji}</span>
                <p className="text-charcoal leading-relaxed">{item.tip}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* The Lantern You Carry - Closing Reflection */}
      <div className="bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-50 rounded-3xl p-8 text-center border border-amber-200 shadow-lg">
        <div className="text-5xl mb-4">🕯️</div>
        <h2 className="font-heading text-2xl font-semibold text-charcoal mb-4">The Lantern You Carry</h2>
        <div className="max-w-2xl mx-auto space-y-4">
          <p className="text-charcoal leading-relaxed">
            Every act of kindness is a lantern — lighting paths for ourselves, our pets, and those we've never met.
          </p>
          <p className="text-stone leading-relaxed">
            Caring for a pet changes how we see the world. It softens our voice, steadies our heart, and reminds us that gentleness is strength.
          </p>
          <p className="font-handwritten text-2xl text-amber-700 mt-6">
            Carry your lantern proudly. Let it shine through your actions.
          </p>
          <div className="pt-4 border-t border-amber-200 mt-6">
            <p className="text-charcoal italic">
              "The light you share today may guide someone home tomorrow."
            </p>
          </div>
          <p className="text-stone text-sm mt-4">🌈 In memory of lights that guide from beyond 🌈</p>
        </div>
        <div className="mt-6 text-4xl">🐾 🕯️ ✨</div>
      </div>
    </div>
  );
};

export default ComfortCornerPage;
